import { useState, useRef } from "react";
import {
  Upload,
  X,
  Loader2,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  KeyRound,
  FileJson,
  ArrowRight,
  XCircle,
  Plug
} from "lucide-react";
import {
  previewWorkflowImport,
  importWorkflows
} from "../../services/workflowService";
import { readJsonFile } from "../../utils/downloadUtils";

/**
 * Import workflows from an Orbit export file.
 *
 * Flow: pick a file -> server dry-run (/import/preview) -> confirm -> import.
 * The preview step is what makes this honest: importing is STRICT, so if any
 * step's endpoint doesn't exist in this org the import is refused outright.
 * Showing that before the user commits is much better than a bare 422.
 */
export default function ImportWorkflowDialog({ open, onClose, onImported }) {
  const fileRef = useRef(null);
  const [bundle, setBundle] = useState(null);
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const reset = () => {
    setBundle(null);
    setFileName("");
    setPreview(null);
    setError(null);
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const close = () => {
    reset();
    onClose?.();
  };

  const handleFile = async (file) => {
    if (!file) return;
    reset();
    setFileName(file.name);
    setBusy(true);
    try {
      const parsed = await readJsonFile(file);
      if (parsed?.format !== "orbit.workflow") {
        setError(
          "That doesn't look like an Orbit workflow export. Pick a file produced by Export."
        );
        setBusy(false);
        return;
      }
      setBundle(parsed);
      const res = await previewWorkflowImport(parsed);
      if (res.success) {
        setPreview(res.data);
      } else {
        setError(res.message);
      }
    } catch (e) {
      setError(e.message || "Could not read that file.");
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async () => {
    setBusy(true);
    setError(null);
    const res = await importWorkflows(bundle);
    setBusy(false);
    if (res.success) {
      onImported?.(res.data);
      close();
      return;
    }
    setError(res.message);
    // A 422 from the strict import carries a fresh report — show it, since it
    // may differ from the preview (e.g. an integration changed in between).
    if (res.preview) setPreview(res.preview);
  };

  const canImport = preview?.importable && !busy;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="shrink-0 px-5 py-4 border-b border-gray-200 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white">
            <Upload className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-gray-900">Import workflows</h2>
            <p className="text-xs text-gray-500">
              Bring in a workflow exported from this or another organization.
              We'll check every step against your catalog first.
            </p>
          </div>
          <button
            onClick={close}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-5 space-y-4">
          {/* File picker */}
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 px-4 py-6 rounded-xl border-2 border-dashed border-gray-300 text-sm font-medium text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/40 transition-colors disabled:opacity-60"
            >
              {busy && !preview ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <FileJson className="w-5 h-5" />
              )}
              {fileName || "Choose an export file (.json)"}
            </button>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Per-workflow report */}
          {preview?.workflows?.map((wf, i) => (
            <WorkflowReport key={`${wf.name}-${i}`} report={wf} />
          ))}

          {preview && !preview.importable && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                Nothing will be imported until every step resolves. Connect the
                missing provider in this organization, or ask the exporting
                organization for a version that only uses shared catalog APIs.
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 px-5 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            onClick={close}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={!canImport}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy && preview ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            Import
            {preview?.workflows?.length > 1 ? ` ${preview.workflows.length}` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}

const STATUS_STYLE = {
  exact: { icon: CheckCircle2, className: "text-green-600" },
  // A different UUID here matches the same provider + method + path.
  remapped: { icon: ArrowRight, className: "text-blue-600" },
  unresolved: { icon: XCircle, className: "text-red-600" }
};

function WorkflowReport({ report }) {
  const bad = report.endpoints?.filter((e) => e.status === "unresolved") || [];
  const notConnected =
    report.endpoints?.filter(
      (e) => e.status !== "unresolved" && e.provider_connected === false
    ) || [];

  return (
    <div
      className={`rounded-xl border p-4 ${
        report.importable
          ? "border-gray-200 bg-white"
          : "border-red-200 bg-red-50/40"
      }`}
    >
      <div className="flex items-center gap-2 mb-3">
        {report.importable ? (
          <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
        ) : (
          <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />
        )}
        <h3 className="font-semibold text-gray-900 truncate">{report.name}</h3>
        {report.name_conflict && (
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 shrink-0">
            name taken — will be numbered
          </span>
        )}
      </div>

      {/* Steps */}
      <ul className="space-y-1.5 mb-1">
        {report.endpoints?.map((e, i) => {
          const style = STATUS_STYLE[e.status] || STATUS_STYLE.unresolved;
          const Icon = style.icon;
          return (
            <li key={`${e.endpoint_id}-${i}`} className="flex items-start gap-2 text-sm">
              <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${style.className}`} />
              <div className="min-w-0">
                <span className="text-gray-800">
                  {e.endpoint_name || e.endpoint_id}
                </span>
                {e.provider_name && (
                  <span className="text-gray-500"> · {e.provider_name}</span>
                )}
                {e.http_method && e.path && (
                  <span className="text-gray-400 text-xs">
                    {" "}
                    {e.http_method} {e.path}
                  </span>
                )}
                {e.reason && (
                  <p className="text-xs text-red-600 mt-0.5">{e.reason}</p>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {/* Warnings that don't block */}
      {!bad.length && notConnected.length > 0 && (
        <p className="flex items-start gap-2 text-xs text-amber-700 mt-2">
          <Plug className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            {notConnected.length} step
            {notConnected.length > 1 ? "s use" : " uses"} a provider you haven't
            connected yet. The workflow imports fine, but can't run until you
            connect it.
          </span>
        </p>
      )}

      {report.redactions?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
            <KeyRound className="w-3.5 h-3.5 text-amber-600" />
            {report.redactions.length} credential
            {report.redactions.length > 1 ? "s were" : " was"} stripped on export
            — re-enter after importing
          </p>
          <ul className="space-y-0.5">
            {report.redactions.map((r, i) => (
              <li key={`${r.path}-${i}`} className="text-xs text-gray-500 font-mono truncate">
                {r.path}
              </li>
            ))}
          </ul>
        </div>
      )}

      {report.errors?.length > 0 && (
        <ul className="mt-2 space-y-1">
          {report.errors.map((e, i) => (
            <li key={i} className="text-xs text-red-700">
              {e}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
